import {
  getStructuredSuggestions,
  IBuildingSuggest,
  ILocationSuggest,
  IRailwaySuggest,
  IStructuredSuggestRequest,
  IUndergroundSuggest,
} from './structured_suggest';

import { isEmpty, omitBy } from 'lodash';

import loadYmapsApi from './ymaps';

export interface IAddressSuggestionOptions {
  boundedBy: [IYmapsCoord, IYmapsCoord];
  results: number;
}

export interface IAddressSuggestion {
  displayName: string;
  type: string;
  value: string;
  hl: IYmapsCoord[];
}

export interface IGetAddressSuggestions {
  (
    value: string,
    options?: IAddressSuggestionOptions,
  ): Promise<IAddressSuggestion[]>;
}

export const getAddressSuggestions: IGetAddressSuggestions = (value, options) =>
  loadYmapsApi(['suggest'])
    .then(ymaps => ymaps.suggest(value, options))
    .then(items => items || []);

export interface IMultiSuggestParams {
  structured: IStructuredSuggestRequest;
  yandex: {
    value: string,
    options?: IAddressSuggestionOptions,
  };
  buildingType: string;
  countrysideSuggestEnabled: boolean;
}

export interface IMultiSuggestResult {
  suggestions: IResultSuggestions;
  sortOrder: string[];
}

export interface IResultSuggestions {
  addresses?: IAddressSuggestion[];
  cities?: ILocationSuggest[];
  districts?: ILocationSuggest[];
  undergrounds?: IUndergroundSuggest[];
  newbuildings?: IBuildingSuggest[];
  businessCenters?: IBuildingSuggest[];
  shoppingCenters?: IBuildingSuggest[];
  railways?: IRailwaySuggest[];
  villages?: IBuildingSuggest[];
  roads?: ILocationSuggest[];
}

let suggestions: IResultSuggestions = {};
let suggestResults: IMultiSuggestResult = {suggestions: {}, sortOrder: []};

function getRailways(railways: IRailwaySuggest[], offerType: string): IRailwaySuggest[] | undefined {
  return offerType === 'commercial' ? undefined : railways;
}

function getVillages( villages: IBuildingSuggest[], buildingType: string ) {
 return ['house', 'sector', 'housepart', 'townhouse'].includes(buildingType) ? villages : undefined;
}

export function getMultiSuggest(
  params: IMultiSuggestParams,
): Promise<IMultiSuggestResult> {
  const { yandex: { value, options }, structured, buildingType, countrysideSuggestEnabled } = params;
  const yandexSuggest = loadYmapsApi(['suggest'])
    .then(ymaps => ymaps.suggest( `Россия, ${value}`, options))
    .then(items => items || [])
    .catch(error => null);
  const structuredSuggest = getStructuredSuggestions(structured)
    .catch(error => null);
  const promises = [yandexSuggest, structuredSuggest];

  return Promise.all(promises as any)
    .then((results: any[]) => {
      const [yandex, structured] = results;
      suggestions = {};
      if (structured && structured.suggestions) {
        const {
          businessCenters,
          cities,
          districts,
          newbuildings,
          shoppingCenters,
          undergrounds,
          railways,
          villages,
          roads,
        } = structured.suggestions;

        suggestions = {
          businessCenters: businessCenters && businessCenters.items,
          cities: cities && cities.items,
          districts: districts && districts.items,
          newbuildings: newbuildings && newbuildings.items,
          railways: railways && getRailways(railways.items, structured.offerType),
          shoppingCenters: shoppingCenters && shoppingCenters.items,
          undergrounds: undergrounds && undergrounds.items,
          villages: countrysideSuggestEnabled
            ? villages && villages.items || undefined
            : villages && getVillages(villages.items, buildingType),
          roads: roads && roads.items,
        };
      }

      suggestions = omitBy(suggestions, isEmpty);

      if (yandex && !isEmpty(yandex)) {
        suggestions.addresses = Object.keys(suggestions).length ? yandex.slice(0, 5) : yandex;
      }

      if (!suggestions.addresses && !structured.suggestions) {
        throw {};
      }

      suggestResults = {
        suggestions,
        sortOrder: isEmpty(suggestions) ? [] : structured && structured.sortOrder || ['addresses'],
      };

      return suggestResults;
    });
}
